import { useEffect, useRef, useState } from 'react';
import { BarChart3, CalendarDays, ClipboardCheck, Donut, PieChart, Table2, RefreshCw } from 'lucide-react';
import { AppLayout } from '../../components/layout';

import './ESSDashboardPage.css';

function metricIcon(name) {
  if (/holiday|calendar/i.test(name)) return CalendarDays;
  if (/attendance|regularization|request/i.test(name)) return ClipboardCheck;
  return BarChart3;
}

function isMetric(item) {
  return item && typeof item.name === 'string' && item.name.trim()
    && (typeof item.count === 'string' || typeof item.count === 'number');
}

function isRecord(value) {
  return value && typeof value === 'object' && !Array.isArray(value);
}

function hasBreakdown(metric) {
  return [metric.breakdown, metric.percentages].some(value => isRecord(value) && Object.keys(value).length > 0);
}

const chartViews = [
  { id: 'table', label: 'Table', icon: Table2 },
  { id: 'pie', label: 'Pie chart', icon: PieChart },
  { id: 'bar', label: 'Bar chart', icon: BarChart3 },
  { id: 'doughnut', label: 'Doughnut chart', icon: Donut },
];

function Breakdown({ name, breakdown, percentages }) {
  const [view, setView] = useState('table');
  const counts = isRecord(breakdown) ? breakdown : {};
  const shares = isRecord(percentages) ? percentages : {};
  const hasPercentages = Object.keys(shares).length > 0;
  const fields = [...new Set([...Object.keys(counts), ...Object.keys(shares)])];
  if (!fields.length) return null;

  const display = value => typeof value === 'string' || typeof value === 'number' ? value : '—';
  const rows = fields.map((field, index) => ({
    field,
    count: display(counts[field]),
    percentage: display(shares[field]),
    weight: typeof counts[field] === 'number' && Number.isFinite(counts[field]) && counts[field] >= 0 ? counts[field] : null,
    color: `hsl(${(index * 137.508 + 225) % 360}, 65%, 48%)`,
  }));
  const total = rows.reduce((sum, row) => sum + (row.weight || 0), 0);
  const canChart = rows.every(row => row.weight !== null) && Number.isFinite(total) && total > 0;
  const activeView = canChart ? view : 'table';
  const chartVisible = activeView !== 'table';
  const maxWeight = Math.max(0, ...rows.map(row => row.weight || 0));
  let position = 0;
  const stops = rows.map(row => {
    const start = position;
    position += total > 0 ? (row.weight / total) * 100 : 0;
    return `${row.color} ${start}% ${position}%`;
  });

  return (
    <div className="ess-dashboard__breakdown">
      <div className="ess-dashboard__breakdown-heading">
        <span>Breakdown</span>
        {canChart && (
          <div className="ess-dashboard__chart-options" role="group" aria-label={`Chart view for ${name}`}>
            {chartViews.map(({ id, label, icon: ViewIcon }) => (
              <button key={id} type="button" className="ess-dashboard__refresh"
                onClick={() => setView(id)} aria-pressed={activeView === id}
                aria-label={`Show ${label.toLowerCase()} for ${name}`} title={label}>
                <ViewIcon size={16} aria-hidden="true" />
              </button>
            ))}
          </div>
        )}
      </div>
      {chartVisible && activeView !== 'bar' && (
        <div className="ess-dashboard__chart">
          <div className={`ess-dashboard__pie${activeView === 'doughnut' ? ' ess-dashboard__pie--doughnut' : ''}`}
            role="img" aria-label={`${name}: ${activeView} chart by count. Details in the following table.`}
            style={{ background: `conic-gradient(${stops.join(', ')})` }} />
        </div>
      )}
      {activeView === 'bar' && (
        <div className="ess-dashboard__bars" role="img" aria-label={`${name}: bar chart by count. Details in the following table.`}>
          {rows.map(row => (
            <div className="ess-dashboard__bar-row" key={row.field}>
              <span className="ess-dashboard__bar-label">{row.field}</span>
              <div className="ess-dashboard__bar-track"><span style={{ width: `${row.weight / maxWeight * 100}%`, background: row.color }} /></div>
              <span className="ess-dashboard__bar-count">{row.count}</span>
            </div>
          ))}
        </div>
      )}
      <div className="ess-dashboard__table-wrap">
        <table className="ess-dashboard__table">
          <caption className="ess-dashboard__sr-only">{name} breakdown</caption>
          <thead><tr><th scope="col">Field name</th><th scope="col">Count</th>{hasPercentages && <th scope="col">Percentage</th>}</tr></thead>
          <tbody>{rows.map(row => (
            <tr key={row.field}>
              <th scope="row">{chartVisible && <span className="ess-dashboard__swatch" style={{ background: row.color }} aria-hidden="true" />}{row.field}</th>
              <td>{row.count}</td>
              {hasPercentages && <td>{row.percentage === '—' ? '—' : `${String(row.percentage).replace(/%$/, '')}%`}</td>}
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
function MetricCard({ metric, getData, onUpdate, firstBreakdown }) {
  const data = metric;
  const countLength = String(data.count).length;
  const valueSize = countLength > 20 ? ' ess-dashboard__value--long' : countLength > 10 ? ' ess-dashboard__value--compact' : '';
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const inFlight = useRef(false);
  const mounted = useRef(false);
  const Icon = metricIcon(metric.name);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);



  async function refresh() {
    if (inFlight.current) return;
    inFlight.current = true;
    setRefreshing(true);
    setError('');
    try {
      const response = await getData(`/dashboards/${encodeURIComponent(metric.name)}`);
      const payload = response?.data;
      const updated = Array.isArray(payload)
        ? payload.find(item => item?.name === metric.name)
        : payload;
      if (!isMetric(updated) || updated.name !== metric.name) {
        throw new Error('The response did not include this metric. Please try again.');
      }
      if (mounted.current) {
        onUpdate(updated);
        setUpdatedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      }
    } catch (err) {
      if (mounted.current) setError(err.message || 'Unable to refresh. Please try again.');
    } finally {
      inFlight.current = false;
      if (mounted.current) setRefreshing(false);
    }
  }

  return (
    <article className={`ess-dashboard__card${hasBreakdown(data) ? ' ess-dashboard__card--breakdown' : ''}${firstBreakdown ? ' ess-dashboard__card--first-breakdown' : ''}`} aria-label={metric.name} aria-busy={refreshing}>
      <div className="ess-dashboard__card-top">
        <span className="ess-dashboard__metric-icon"><Icon size={21} aria-hidden="true" /></span>
        {data.period != null && String(data.period).trim() && <span className="ess-dashboard__period">{String(data.period)}</span>}
        <button
          type="button"
          className="ess-dashboard__refresh"
          onClick={refresh}
          disabled={refreshing}
          aria-label={`Refresh ${metric.name}`}
          title={`Refresh ${metric.name}`}
        >
          <RefreshCw size={16} className={refreshing ? 'ess-dashboard__spin' : ''} aria-hidden="true" />
        </button>
      </div>
      <h3 className="ess-dashboard__label">{metric.name}</h3>
      <p className={`ess-dashboard__value${valueSize}`} aria-live="polite">{data.count}</p>
      <Breakdown name={metric.name} breakdown={data.breakdown} percentages={data.percentages} />
      <div className="ess-dashboard__card-footer">
        {error ? <p className="ess-dashboard__error" role="alert">{error} Last value retained.</p> : (
          <p className="ess-dashboard__note" role="status">
            {refreshing ? 'Refreshing...' : updatedAt ? `Updated at ${updatedAt}` : 'Latest overview'}
          </p>
        )}
      </div>
    </article>
  );
}

export default function LiveDashboardPage({ title, description, icon: Icon, getData, intro = '' }) {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getData('/dashboards')
      .then(response => {
        if (!Array.isArray(response?.data) || !response.data.every(isMetric)) {
          throw new Error('Dashboard data is unavailable. Please try again.');
        }
        if (!cancelled) setMetrics(response.data);
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Unable to load your dashboard. Please try again.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [attempt, getData]);

  const orderedMetrics = [
    ...metrics.filter(metric => !hasBreakdown(metric)),
    ...metrics.filter(hasBreakdown),
  ];
  const firstBreakdownIndex = orderedMetrics.findIndex(hasBreakdown);

  function updateMetric(updated) {
    setMetrics(current => current.map(metric => metric.name === updated.name ? updated : metric));
  }

  return (
    <AppLayout>
      <section className="ess-dashboard" aria-label={`${title} dashboard`}>
        <header className="ess-dashboard__header">
          <div className="ess-dashboard__heading">
            <span className="ess-dashboard__hero-icon"><Icon size={26} aria-hidden="true" /></span>
            <div>
              <p className="ess-dashboard__eyebrow">{description}</p>
              <h1>{title} Dashboard</h1>
            </div>
          </div>
          <p className="ess-dashboard__intro">{intro}</p>
        </header>

        <div className="ess-dashboard__section-heading">
          <h2>Your overview</h2>
          <p>Refresh any card for the latest update.</p>
        </div>

        {loading ? (
          <div role="status" aria-label="Loading dashboard">
            <span className="ess-dashboard__sr-only">Loading dashboard...</span>
            <div className="ess-dashboard__grid" aria-hidden="true">
              {[0, 1, 2].map(item => (
                <div key={item} className="ess-dashboard__card ess-dashboard__skeleton">
                  <span /><span /><span />
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="ess-dashboard__state">
            <p className="ess-dashboard__error" role="alert">{error}</p>
            <button type="button" className="ess-dashboard__retry" onClick={() => setAttempt(value => value + 1)}>
              <RefreshCw size={15} aria-hidden="true" /> Try again
            </button>
          </div>
        ) : metrics.length === 0 ? (
          <div className="ess-dashboard__state">
            <BarChart3 size={30} aria-hidden="true" />
            <h2>No dashboard data yet</h2>
            <p>Your overview will appear here when data is available.</p>
            <button type="button" className="ess-dashboard__retry" onClick={() => setAttempt(value => value + 1)}>
              <RefreshCw size={15} aria-hidden="true" /> Check again
            </button>
          </div>
        ) : (
          <div className="ess-dashboard__grid">
            {orderedMetrics.map((metric, index) => <MetricCard key={metric.name} metric={metric} getData={getData} onUpdate={updateMetric} firstBreakdown={index === firstBreakdownIndex} />)}
          </div>
        )}
      </section>
    </AppLayout>
  );
}
